import { Nullable } from 'global';
import { formatStringToNumber } from 'utils/formatters';
import { DiscountType } from '@trnsact/trnsact-shared-types';
import { ChargeAttachType, Equipment } from 'modules/equipment/types';
import { chargeTypesByLabels, newChargeRow } from '../../equipment';
import { Quote, QuoteChargeListingEntity, QuoteChargeType } from '../types';

export function mapEquipmentToInvoiceTable(
  equipment: Record<string, any>,
  quote: Nullable<Quote>
): QuoteChargeListingEntity {
  const priceFromQuote = (quote?.equipment ?? []).find(
    quoteEquipment => quoteEquipment.equipmentId === equipment.equipmentId
  )?.price;

  return {
    charge: null,
    type: QuoteChargeType.Equipment,
    equipment: {
      ...equipment,
      price: priceFromQuote ?? {
        ...newChargeRow,
        wholesalePrice: equipment.wholesaleCost,
      },
      attachments: (equipment.attachments ?? []).map((attachment: any) => ({
        ...attachment,
        price: {
          ...newChargeRow,
          retailPrice: attachment.retailPrice ?? 0,
          discount: (attachment as any).discountValue ?? 0,
          wholesalePrice: formatStringToNumber(attachment.unitCost) ?? 0,
          discountType: (attachment as any).discountType ?? DiscountType.Flat,
        },
      })),
    },
  };
}

export function mapEquipmentAndQuoteListToInvoiceTable(
  VOId: string,
  list: Equipment[],
  quote: Nullable<Quote>
): QuoteChargeListingEntity[] {
  if (!Array.isArray(list) || !list.length) return [];

  const equipmentList: QuoteChargeListingEntity[] = list.map(entity => mapEquipmentToInvoiceTable(entity, quote));

  const feesInvoiceList = (quote?.fees ?? []).map(fee => {
    return {
      charge: {
        type: fee.type,
        label: fee.description,
        price: fee?.price ?? newChargeRow,
        retailPrice: fee?.price?.wholesalePrice ?? 0,
        wholesalePrice: fee?.price?.wholesalePrice ?? 0,
        financeType: chargeTypesByLabels[fee.description],
        attachTo: {
          id: fee.equipmentId ?? VOId,
          type: fee.equipmentId ? ChargeAttachType.Equipment : ChargeAttachType.Invoice,
        },
      },
      type: QuoteChargeType.Invoice,
      equipment: null,
    };
  });

  const result: QuoteChargeListingEntity[] = [...equipmentList];

  for (const invoice of feesInvoiceList) {
    const { attachTo } = invoice.charge!;

    if (attachTo.type === ChargeAttachType.Equipment) {
      const index = result.findIndex(
        row => row.type === QuoteChargeType.Equipment && row.equipment?.equipmentId === attachTo.id
      );

      if (index !== -1) {
        result.splice(index + 1, 0, invoice);
        continue;
      }
    }

    result.push(invoice);
  }

  return result;
}
